import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type T3pResource } from '@atlassian/search-client/fetch-search-config';

import {
	type ForgeConnectorProductKey,
	FULL_CONNECTOR_SOURCE,
	type PlatformIntegrationAri,
	registerForgeConnectorIcon,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../common/types/schemas/types';
import { get3PLogoFromURL } from '../../common/ui/icons';
import { mapAtisToTypeFilterConfig } from '../../common/utils/filters/universalType';

export type OnForgeConnectorTypeMappingFn = (
	productKey: ForgeConnectorProductKey,
	typeFilterConfig: ReturnType<typeof mapAtisToTypeFilterConfig>,
) => void;

/**
 * Map a single Forge connector resource onto its third-party config entry.
 *
 * Returns `undefined` when the resource is missing the minimum fields needed
 * to render (`lightIcon`, `workspaceName`) — callers should treat that as
 * "drop this connector silently".
 *
 * Side effects (intentional, in this order):
 *   1. `registerForgeConnectorIcon(productKey, icon)` — always registers the
 *      icon in the in-memory icon registry so `ProductOrNounIcon` can find it
 *      without prop drilling. Idempotent per `productKey`.
 *   2. `onForgeConnectorTypeMapping(productKey, typeFilterConfig)` —
 *      only when both the callback is supplied **and**
 *      `fg('rovo-search-dynamic-forge-config')` is on. Idempotent per
 *      `productKey` (the underlying cache uses `.includes` for product dedupe
 *      and `Set`-based dedupe for sub-categories).
 */
export const mapForgeConnectorToThirdPartyConfig = (
	forgeConnector: T3pResource,
	onForgeConnectorTypeMapping?: OnForgeConnectorTypeMappingFn,
): ThirdPartyConfigsWithAPIResponse | undefined => {
	if (!forgeConnector.lightIcon || !forgeConnector.workspaceName) {
		return undefined;
	}

	const icon = get3PLogoFromURL(forgeConnector.lightIcon, forgeConnector.workspaceName, 'medium');
	const searchResultIcon = get3PLogoFromURL(
		forgeConnector.lightIcon,
		forgeConnector.providerId,
		'large',
	);

	const productKey = forgeConnector.type as ForgeConnectorProductKey;

	registerForgeConnectorIcon(productKey, icon);

	const typeFilterConfig = forgeConnector.entityTypes
		? mapAtisToTypeFilterConfig(forgeConnector.entityTypes)
		: new Map();

	if (onForgeConnectorTypeMapping && fg('rovo-search-dynamic-forge-config')) {
		onForgeConnectorTypeMapping(productKey, typeFilterConfig);
	}

	const config: ThirdPartyConfigsWithAPIResponse = {
		id: forgeConnector.type,
		displayName:
			forgeConnector.environmentType && !fg('rovo-search-team-eu-artifacts-cosmetics')
				? `${forgeConnector.workspaceName} (${forgeConnector.environmentType})`
				: forgeConnector.workspaceName,
		resourceType: forgeConnector.type,
		datasourceId: forgeConnector.datasourceId,
		connectionId: forgeConnector.connectionId,
		providerId: forgeConnector.providerId,
		outboundAuthUrl: forgeConnector.outboundAuthUrl,
		// `forgeConnector.integrationId` is a `string` (the wire shape).
		// `PlatformIntegrationAri` is a branded nominal type. The value at
		// this site is already an integration ARI (built server-side by the
		// platform integration registry), so the cast is structurally safe;
		// the `unknown` hop exists only because TS rejects direct
		// `string`-to-branded assignment. Same pattern is used in
		// `adapter.ts`. RAOC-22 follow-up: tighten in search-client by
		// exposing a constructor for branded ARIs.
		integrationAri: forgeConnector.integrationId
			? (forgeConnector.integrationId as unknown as PlatformIntegrationAri)
			: (`ari:cloud:platform::integration/${forgeConnector.providerId}` as PlatformIntegrationAri),
		filters: ['lastUpdated', 'universalTypeFilter'],
		displayWorkspaceNameAsProductName: true,
		isEnabled: () => true,
		userNeedsOAuth: !forgeConnector.isUserOAuthed,
		requiresOauth: !forgeConnector.isUserOAuthed && !forgeConnector.outboundAuthUrl ? false : true,
		icon,
		iconUrl: forgeConnector.lightIcon,
		searchResultIcon,
		typeFilterConfig,
		workspaceName: forgeConnector.workspaceName,
		connectorSources: [FULL_CONNECTOR_SOURCE],
	};

	return config;
};
